/**
 * Hook for session-only legend visibility in parent-controlled mode.
 *
 * Preferences define persisted visibility; session state holds live legend
 * masking until reset.
 *
 * @packageDocumentation
 * @module useCurveSessionVisibility
 * @public
 */

import { useCallback, useEffect, useState } from 'react';
import { CurveId, PlotPreferencesV2 } from '../config/plotTypes';
import {
  clearCurveSessionVisibility,
  CurveSessionVisibility,
  pruneCurveSessionVisibility,
  toggleCurveSessionVisibility,
} from './curveSessionVisibility';

// -- type definitions --

/** Options for {@link useCurveSessionVisibility}. */
export interface UseCurveSessionVisibilityOptions {
  plotPreferences: PlotPreferencesV2;
}

/** Result of {@link useCurveSessionVisibility}. */
export interface UseCurveSessionVisibilityResult {
  /** Live legend visibility overrides keyed by curve id. */
  curveSessionVisibility: CurveSessionVisibility;
  /** Pass to {@link ScatterPlotComponent} as `onToggleCurveSessionVisibility`. */
  toggleCurveSessionVisibility: (curveIds: readonly CurveId[]) => void;
  /** Pass to {@link ScatterPlotComponent} as `onResetCurveSessionVisibility`. */
  resetCurveSessionVisibility: () => void;
}

/**
 * Manage session legend visibility in controlled
 * {@link ScatterPlotComponent} mode.
 */
export function useCurveSessionVisibility({
  plotPreferences,
}: UseCurveSessionVisibilityOptions): UseCurveSessionVisibilityResult {
  const [curveSessionVisibility, setCurveSessionVisibility] =
    useState<CurveSessionVisibility>({});

  useEffect(() => {
    setCurveSessionVisibility((previous) =>
      pruneCurveSessionVisibility(previous, plotPreferences)
    );
  }, [plotPreferences]);

  const handleToggleCurveSessionVisibility = useCallback(
    (curveIds: readonly CurveId[]) => {
      setCurveSessionVisibility((previous) =>
        toggleCurveSessionVisibility(previous, curveIds)
      );
    },
    []
  );

  const resetCurveSessionVisibility = useCallback(() => {
    setCurveSessionVisibility(clearCurveSessionVisibility());
  }, []);

  return {
    curveSessionVisibility,
    toggleCurveSessionVisibility: handleToggleCurveSessionVisibility,
    resetCurveSessionVisibility,
  };
}
